import { computed, Injectable, inject } from '@angular/core';
import { PreloadConfigService } from '@common/config/preload-config.service';
import { ApiVersion } from '@common/interfaces/api-version.interface';
import { MapSource } from './map-sources';
import { RuntimeMode, SessionContextService } from './session-context.service';

export type InOutAction = 'in' | 'out';
export type CheckInOutAction = 'check-in' | 'check-out';

export interface ApiRouteContextData {
  device: RuntimeMode;
  companyId: number;
  workerId?: number;
}

export interface ApiRouteResult<TParams extends Record<string, unknown> = Record<string, unknown>> {
  path: string;
  context: ApiRouteContextData;
  params: TParams;
}

@Injectable({
  providedIn: 'root',
})
export class ApiRouteService {
  private readonly preConfServ = inject(PreloadConfigService);
  private readonly sessionContext = inject(SessionContextService);

  private readonly version: ApiVersion = 'v2';

  public readonly webBase = computed(() => this.preConfServ.config()?.apiUrl);
  // Las rutas V2 del kiosk viven en `api/v2/{companyId}/station/...`, no bajo apiProneUrl
  // (`api/station/`, que solo sirve a las rutas legacy de HttpFichaService.getBackendUrl).
  public readonly kioskBase = computed(() => {
    const config = this.preConfServ.config();
    return config?.apiStationV2Url ?? config?.apiUrl;
  });

  // * =================================================================================================
  // * AUTH ROUTES
  // * =================================================================================================

  public authLogin(): ApiRouteResult<Record<string, never>> {
    return this.buildAuthRoute('authLogin', 'login');
  }

  public authLogout(): ApiRouteResult<Record<string, never>> {
    return this.buildAuthRoute('authLogout', 'logout');
  }

  public authLogoutAll(): ApiRouteResult<Record<string, never>> {
    return this.buildAuthRoute('authLogoutAll', 'logout-all');
  }

  public authMe(): ApiRouteResult<Record<string, never>> {
    return this.buildAuthRoute('authMe', 'me');
  }

  public authSession(): ApiRouteResult<Record<string, never>> {
    return this.buildAuthRoute('authSession', 'session');
  }

  public authForgotPassword(): ApiRouteResult<Record<string, never>> {
    return this.buildAuthRoute('authForgotPassword', 'forgot-password');
  }

  public authResetPassword(): ApiRouteResult<Record<string, never>> {
    return this.buildAuthRoute('authResetPassword', 'reset-password');
  }

  public authChangePassword(): ApiRouteResult<Record<string, never>> {
    return this.buildAuthRoute('authChangePassword', 'change-password');
  }

  /**
   * Ruta del endpoint de Sanctum que planta las cookies `XSRF-TOKEN` y de
   * sesión (`fichanet_dev_session`). Vive en la raíz del dominio del backend
   * (`/sanctum/csrf-cookie`), NO bajo el prefijo `/api/v1` como el resto de
   * rutas de auth. Como SPA y API comparten el mismo origen via el gateway,
   * se usa una ruta relativa directa.
   */
  public authCsrfCookie(): ApiRouteResult<Record<string, never>> {

    return {
      path: '/sanctum/csrf-cookie',
      context: {
        device: 'web',
        companyId: this.sessionContext.getCompanyId('web') ?? 0,
        workerId: this.sessionContext.getWorkerId(),
      },
      params: {},
    };
  }

  // * =================================================================================================
  // * FICHAJE ROUTES
  // * =================================================================================================

  /**
   * Ruta V2 del mapa: `/api/v2/{companyId}/{source}/calendar/map`.
   * `source` es el componente origen de la petición (contrato cerrado, ver `map-sources.ts`):
   * el backend responde 404 a valores fuera del contrato.
   */
  public calendarMap(source: MapSource): ApiRouteResult<Record<string, never>> {
    return this.buildRoute('calendarMap', [source, 'calendar', 'map'], {});
  }

  /**
   * URL completa (con query) de la imagen de mapa para unas coordenadas: la que consume `<img [imgHttpToken]>`.
   * Lanza si no hay companyId resoluble (igual que `calendarMap`).
   */
  public calendarMapUrl(source: MapSource, latitude: number, longitude: number, zoom = 16): string {
    return `${this.calendarMap(source).path}?lat=${latitude}&lng=${longitude}&zoom=${zoom}`;
  }

  public canonicalEvents(isStation: boolean, companyId?: number): ApiRouteResult<Record<string, never>> {
    if (isStation) {
      return this.buildCompanyScopedRoute('kiosk', 'canonicalEvents', ['calendar', 'events', 'canonical'], {}, companyId);
    }

    return this.buildWebRoute('canonicalEvents', ['calendar', 'events', 'canonical'], {}, companyId);
  }

  public checkInOut(
    checkType: CheckInOutAction,
    scheduleId: number,
    isStation = false,
    companyId?: number,
  ): ApiRouteResult<{ checkType: CheckInOutAction; scheduleId: number }> {
    // En el kiosk no hay `user` en localStorage: la empresa llega del evento.
    if (isStation) {
      return this.buildCompanyScopedRoute('kiosk', 'checkInOut', ['shifts', scheduleId, checkType], { checkType, scheduleId }, companyId);
    }

    return this.buildRoute('checkInOut', ['shifts', scheduleId, checkType], { checkType, scheduleId });
  }

  public shiftRemoteWork(scheduleId: number): ApiRouteResult<{ scheduleId: number }> {
    return this.buildRoute('shiftRemoteWork', ['shifts', scheduleId, 'remote-work'], { scheduleId });
  }

  public shiftCustomer(
    scheduleId: number,
    isStation = false,
    companyId?: number,
  ): ApiRouteResult<{ scheduleId: number }> {
    return this.buildCompanyScopedRoute(
      isStation ? 'kiosk' : 'web',
      'shiftCustomer',
      ['shifts', scheduleId, 'customer'],
      { scheduleId },
      companyId,
    );
  }

  public customersSearch(
    isStation = false,
    companyId?: number,
  ): ApiRouteResult<Record<string, never>> {
    return this.buildCompanyScopedRoute(
      isStation ? 'kiosk' : 'web',
      'customersSearch',
      ['customers', 'search'],
      {},
      companyId,
    );
  }

  public permissionInOut(
    inOut: InOutAction,
    scheduleId: number,
    isStation = false,
    companyId?: number,
  ): ApiRouteResult<{ inOut: InOutAction; scheduleId: number }> {
    const checkType: CheckInOutAction = inOut === 'in' ? 'check-in' : 'check-out';
    return this.buildCompanyScopedRoute(
      isStation ? 'kiosk' : 'web',
      'permissionInOut',
      ['permissions', scheduleId, checkType],
      { inOut, scheduleId },
      companyId,
    );
  }

  public breakInOut(
    inOut: InOutAction,
    isStation = false,
    companyId?: number,
  ): ApiRouteResult<{ inOut: InOutAction; checkType: CheckInOutAction }> {
    const checkType: CheckInOutAction = inOut === 'in' ? 'check-in' : 'check-out';
    return this.buildCompanyScopedRoute(
      isStation ? 'kiosk' : 'web',
      'breakInOut',
      ['breaks', checkType],
      { inOut, checkType },
      companyId,
    );
  }

  public extraHoursInOut(
    inOut: InOutAction,
    isStation = false,
    companyId?: number,
  ): ApiRouteResult<{ inOut: InOutAction; checkType: CheckInOutAction }> {
    const checkType: CheckInOutAction = inOut === 'in' ? 'check-in' : 'check-out';

    return this.buildCompanyScopedRoute(
      isStation ? 'kiosk' : 'web',
      'extraHoursInOut',
      ['extra-hours', checkType],
      { inOut, checkType },
      companyId,
    );
  }

  public reasons(
    reasonType: string,
    isStation = false,
    companyId?: number,
  ): ApiRouteResult<{ reasonType: string }> {
    return this.buildCompanyScopedRoute(
      isStation ? 'kiosk' : 'web',
      'reasons',
      ['reasons', reasonType],
      { reasonType },
      companyId,
    );
  }

  public workersListRoot(companyId: number): ApiRouteResult<{ companyId: number }> {
    return this.buildWebRoute('workersListRoot', ['workers', 'list', 'root'], { companyId }, companyId);
  }

  /**
   * Listado de solicitudes (vacaciones/IT/otros) de la empresa activa.
   * `POST /api/v2/{companyId}/list`. Equivalente V2 de la V1
   * `POST /request/{companyID}/list` (RequestHolidaysItController@listRequest).
   */
  public requestsList(): ApiRouteResult<Record<string, never>> {
    return this.buildRoute('requestsList', ['list'], {});
  }

  /**
   * Rechazar días concretos de una solicitud de vacaciones/dld/it ya
   * existente. `POST /api/v2/{companyId}/{workerId}/holidays/rejected`.
   * Equivalente V2 de la V1 `POST /request/{workerId}/holidays/rejected`
   * (RequestHolidaysItController@rejected), ya migrado al backend
   * (`V2\RequestHolidaysItController@rejected` + `RejectHolidaysItDaysAction`).
   */
  public rejectHolidayRequestDays(workerId: number): ApiRouteResult<{ workerId: number }> {
    return this.buildRoute('rejectHolidayRequestDays', [workerId, 'holidays', 'rejected'], { workerId });
  }

  /**
   * Aceptar días concretos de una solicitud de vacaciones ya existente.
   * `POST /api/v2/{companyId}/{workerId}/holidays/accepted`. Equivalente V2
   * de la V1 `POST /request/{workerId}/holidays/accepted`
   * (RequestHolidaysItController@accepted), ya migrado al backend
   * (`V2\RequestHolidaysItController@accepted` + `AcceptHolidaysItAction`).
   */
  public acceptHolidayRequestDays(workerId: number): ApiRouteResult<{ workerId: number }> {
    return this.buildRoute('acceptHolidayRequestDays', [workerId, 'holidays', 'accepted'], { workerId });
  }

  /**
   * Rutas de suplantación (`/api/v2/auth/access-as/{companyId}/{userId}` y `/api/v2/auth/back/{userId}`).
   * Ahora viven en el namespace `/api/v2/auth/` del backend, igual que el resto
   * de rutas de autenticación. El gateway single-entry-point las enruta a través
   * del location /api/ genérico.
   */
  public impersonateAccessAs(companyId: number, userId: number): ApiRouteResult<{ companyId: number; userId: number }> {
    const base = this.webBase();
    if (!base) {
      throw new Error('[ApiRouteService] Missing web base URL for impersonateAccessAs');
    }
    const path = this.join(this.join(this.join(this.join(this.join(base, String(this.version)), 'auth'), 'access-as'), String(companyId)), String(userId));
    return {
      path,
      context: { device: 'web', companyId, workerId: this.sessionContext.getWorkerId() },
      params: { companyId, userId },
    };
  }

  public impersonateBack(userId: number): ApiRouteResult<{ userId: number }> {
    const base = this.webBase();
    if (!base) {
      throw new Error('[ApiRouteService] Missing web base URL for impersonateBack');
    }
    const path = this.join(this.join(this.join(this.join(base, String(this.version)), 'auth'), 'back'), String(userId));
    return {
      path,
      context: { device: 'web', companyId: this.sessionContext.getCompanyId('web') ?? 0, workerId: this.sessionContext.getWorkerId() },
      params: { userId },
    };
  }

  public shiftImages(scheduleId: number, workerId?: number): ApiRouteResult<{ scheduleId: number; workerId: number }> {
    const resolvedWorkerId = workerId ?? this.sessionContext.requireWorkerId('shiftImages');
    return this.buildRoute('shiftImages', ['shifts', scheduleId, 'images', resolvedWorkerId], {
      scheduleId,
      workerId: resolvedWorkerId,
    });
  }

  public optionalScheduleSelect(
    workerId: number,
    companyId?: number,
    isStation = false,
  ): ApiRouteResult<{ workerId: number }> {
    return this.buildCompanyScopedRoute(
      isStation ? 'kiosk' : 'web',
      'optionalScheduleSelect',
      ['shifts', 'workers', workerId, 'optional-schedules', 'select'],
      { workerId },
      companyId,
    );
  }

  public optionalScheduleRelease(
    workerId: number,
    companyId?: number,
    isStation = false,
  ): ApiRouteResult<{ workerId: number }> {
    return this.buildCompanyScopedRoute(
      isStation ? 'kiosk' : 'web',
      'optionalScheduleRelease',
      ['shifts', 'workers', workerId, 'optional-schedules', 'release'],
      { workerId },
      companyId,
    );
  }


  private buildRoute<TParams extends Record<string, unknown>>(
    operation: string,
    segments: Array<string | number>,
    params: TParams,
  ): ApiRouteResult<TParams> {
    const context = this.readContext(operation);
    let path = this.companyBasePath(context.device, context.companyId, operation);

    for (const segment of segments) {
      path = this.join(path, String(segment));
    }

    return { path, context, params };
  }

  private buildCompanyScopedRoute<TParams extends Record<string, unknown>>(
    device: RuntimeMode,
    operation: string,
    segments: Array<string | number>,
    params: TParams,
    companyIdOverride?: number,
  ): ApiRouteResult<TParams> {
    const context = this.readContextForDevice(device, operation, companyIdOverride);
    let path = this.companyBasePath(device, context.companyId, operation);

    for (const segment of segments) {
      path = this.join(path, String(segment));
    }

    return { path, context, params };
  }


  private buildAuthRoute(operation: string, endpoint: string): ApiRouteResult<Record<string, never>> {
    const base = this.webBase();
    if (!base) {
      throw new Error(`[ApiRouteService] Missing web base URL for ${operation}`);
    }

    const path = this.join(this.join(this.join(base, String(this.version)), 'auth'), endpoint);

    return {
      path,
      context: {
        device: 'web',
        companyId: this.sessionContext.getCompanyId('web') ?? 0,
        workerId: this.sessionContext.getWorkerId(),
      },
      params: {},
    };
  }

  private buildWebRoute<TParams extends Record<string, unknown>>(
    operation: string,
    segments: Array<string | number>,
    params: TParams,
    companyIdOverride?: number,
  ): ApiRouteResult<TParams> {
    const context = this.readWebContext(operation, companyIdOverride);
    const base = this.webBase();
    if (!base) {
      throw new Error(`[ApiRouteService] Missing base URL for ${operation} (web)`);
    }

    let path = this.join(base, String(this.version));
    path = this.join(path, String(context.companyId));

    for (const segment of segments) {
      path = this.join(path, String(segment));
    }

    return { path, context, params };
  }

  //* `{base}v2/{companyId}` en web y `{base}v2/{companyId}/station` en kiosk.
  private companyBasePath(device: RuntimeMode, companyId: number, operation: string): string {
    const base = this.resolveBase(device, operation);
    const path = this.join(this.join(base, String(this.version)), String(companyId));
    return device === 'kiosk' ? this.join(path, 'station') : path;
  }

  private readContext(operation: string): ApiRouteContextData {
    const companyId = this.sessionContext.getCompanyId();
    if (companyId === undefined || companyId === null) {
      throw new Error(`[ApiRouteService] Missing companyId for ${operation}`);
    }

    return {
      device: this.sessionContext.mode(),
      companyId,
      workerId: this.sessionContext.getWorkerId(),
    };
  }

  private readWebContext(operation: string, companyIdOverride?: number): ApiRouteContextData {
    const companyId = companyIdOverride ?? this.sessionContext.getCompanyId('web');
    if (companyId === undefined || companyId === null) {
      throw new Error(`[ApiRouteService] Missing companyId for ${operation} (web)`);
    }

    return {
      device: 'web',
      companyId,
      workerId: this.sessionContext.getWorkerId(),
    };
  }

  private readContextForDevice(
    device: RuntimeMode,
    operation: string,
    companyIdOverride?: number,
  ): ApiRouteContextData {
    const companyId = companyIdOverride ?? this.sessionContext.getCompanyId(device);
    if (companyId === undefined || companyId === null) {
      throw new Error(`[ApiRouteService] Missing companyId for ${operation} (${device})`);
    }

    return {
      device,
      companyId,
      workerId: this.sessionContext.getWorkerId(),
    };
  }


  private resolveBase(device: RuntimeMode, operation: string): string {
    const base = device === 'kiosk' ? this.kioskBase() : this.webBase();
    if (!base) {
      throw new Error(`[ApiRouteService] Missing base URL for ${operation} (${device})`);
    }

    return base;
  }

  private join(base: string, segment: string): string {
    const normalizedBase = base.replace(/\/+$/g, '');
    const normalizedSegment = segment.replace(/^\/+|\/+$/g, '');

    if (!normalizedSegment) {
      return normalizedBase;
    }

    return `${normalizedBase}/${normalizedSegment}`;
  }
}
